import {
  HStack,
  StackProps,
  Text,
  useDisclosure,
  VStack,
} from '@chakra-ui/react';
import React from 'react';

import { ImageModal } from '../containers/modals/Image';
import { useSizeContext } from '../containers/SizeProvider';
import { EngeneFileObject } from '../models/EngeneFileObject';
import { AttachFile } from '../models/gen/attachFile';
import { FileKind } from '../models/gen/fileKind';
import { DownloadButton, ViewButton } from './StyledButton';

/**
 * 添付ファイル一覧エリアプロパティ
 */
type AttachedFilesProps = {
  /**
   * ファイル種別
   */
  fileKind: FileKind;
  /**
   * ファイル名リスト
   */
  files?: Array<string | AttachFile>;
  /**
   * ID
   */
  id?: number;
  stackProps?: StackProps;
};

/**
 * 添付ファイル一覧エリア
 *
 * @returns
 */
const AttachedFiles: React.FunctionComponent<AttachedFilesProps> = (props) => {
  const sizes = useSizeContext();

  const [selectedFile, setSelectedFile] = React.useState<EngeneFileObject>();
  const {
    isOpen: isOpenImage,
    onOpen,
    onClose: onCloseImage,
  } = useDisclosure();

  const onOpenImageModal = React.useCallback<Consumer<EngeneFileObject>>(
    (file) => {
      setSelectedFile(file);
      onOpen();
    },
    []
  );
  const onCloseImageModal = React.useCallback(() => {
    setSelectedFile(undefined);
    onCloseImage();
  }, []);

  return (
    <VStack width="full" {...props.stackProps}>
      <Text
        fontSize={sizes.fontSize.modalBodyTitle}
        fontWeight="bold"
        hidden={!Array.isArray(props.files) || props.files.length === 0}
      >
        添付ファイル
      </Text>
      {props.files?.map((attachFile, idx) => (
        <FileArea
          fileKind={props.fileKind}
          file={attachFile}
          id={props.id}
          key={idx}
          onOpenImageModal={onOpenImageModal}
        />
      ))}
      <ImageModal
        file={selectedFile}
        isOpen={isOpenImage}
        onClose={onCloseImageModal}
      />
    </VStack>
  );
};

type FileAreaProps = {
  /**
   * ファイル種別
   */
  fileKind: FileKind;
  /**
   * ファイル名リスト
   */
  file?: string | AttachFile;
  /**
   * ID
   */
  id?: number;
  key?: React.Key;
  onOpenImageModal: Consumer<EngeneFileObject>;
};
const FileArea: React.FunctionComponent<FileAreaProps> = React.memo(
  (props: FileAreaProps) => {
    const fileName =
      typeof props.file === 'string' ? props.file : props.file?.fileName;
    const id = typeof props.file === 'string' ? props.id : props.file?.id;
    const query = React.useMemo(
      () => ({
        mode: props.fileKind,
        id: id,
        filename: fileName,
      }),
      [props.fileKind, fileName, id]
    );

    return (
      <HStack justifyContent="space-between" key={props.key} width="full">
        <Text>{fileName}</Text>
        <HStack justifyContent="space-between" paddingBottom={1}>
          <ViewButton
            fileName={fileName}
            onOpenImageModal={props.onOpenImageModal}
            query={query}
            size="sm"
          />
          <DownloadButton fileName={fileName} query={query} size="sm" />
        </HStack>
      </HStack>
    );
  }
);

export default React.memo(AttachedFiles);
